import { useState, useEffect, useCallback } from "react";
import { ResponseFeed } from "./types/feed";
import { League } from "./types/football_table";
import { CONFIG } from "../../config";
import { BetType } from "../../types/common";
import { feedQueryString } from "../../utils/feedQuery";

export function useGetData(
  defaultTableType: "live" | "today" | "early",
  sport = "fb",
  provider?: string,
  betType?: BetType,
) {
  const [data, setData] = useState<League[]>([]); // data
  const [focusMatchId, setFocusMatchId] = useState<string>("");
  const [tableType, setTableType] = useState<"live" | "today" | "early">(
    defaultTableType,
  );
  const [isLoading, setIsLoading] = useState(false);
  const getData = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await fetch(
        // `https://odds-feed.narongchai-pla.workers.dev/ibc/soccer/${type}`,
        `${CONFIG.API_URL}/c/m/feed/${sport}/${tableType}${feedQueryString(betType, provider, sport)}`,
        // `https://dev-form-odds.askmebet.cloud/form/soccer/${type}`,
      );
      const data = (await res.json()) as ResponseFeed;

      const dataArray: any[] = format(data?.data);

      setData(dataArray);
    } catch (error) {
      console.log("error", error);
    } finally {
      setIsLoading(false);
    }
  }, [betType, provider, sport, tableType]);

  const handleSetFocusMatchId = useCallback(
    (matchId: string) => {
      if (`${matchId}` === `${focusMatchId}`) {
        setFocusMatchId("");
      } else {
        setFocusMatchId(matchId);
      }
    },
    [focusMatchId],
  );

  useEffect(() => {
    getData();
  }, [getData]);

  return {
    focusMatchId,
    setFocusMatchId: handleSetFocusMatchId,
    getData,
    data,
    isLoading,
    tableType,
    setTableType,
  };
}

export const format = (
  data: any,
  tableType = "today",
  lang: string = "en",
  favoriteLeagueId: string[] = [],
): League[] => {
  const result: League[] =
    data?.map((league: any) => {
      const marketName: "bp" | "bpl" = tableType === "live" ? "bpl" : "bp";

      return {
        flag_id: league.fid,
        flag_img: league?.fimg,
        id: league.k,
        league: league.nn,
        matches_no: league.ms,
        is_favorite: favoriteLeagueId.includes(`${league.k}`),
        matches: league.m.map((match: any) => ({
          id: match.k,
          leagueId: league.k,
          isChannel: match.isChannel,
          has_tips: Boolean(match?.tip),
          st: match.st,
          date: match.od,
          live_time: match.i.lt,
          home: match.n?.[lang]?.h,
          home_flag_id: match.n.fhid,
          home_flag_img: match.n?.fhimg,
          home_score: match.i?.h,
          home_hrc: match.i?.hrc,
          away: match.n?.[lang]?.a,
          away_flag_id: match.n.faid,
          away_flag_img: match.n?.faimg,
          away_score: match.i?.a,
          away_arc: match.i?.arc,
          special_prices_no: match.msp ?? 0,
          provider: match.pvd,
          home_en: match.n?.["en"]?.h,
          home_th: match.n?.["th"]?.h,
          away_en: match.n?.["en"]?.a,
          away_th: match.n?.["th"]?.a,
          bet_prices:
            match?.[marketName]?.[0] ?
              match?.[marketName]?.map((market: any) => ({
                // half time
                ...(market?.ah1st ?
                  {
                    ah1st: {
                      sp: market.ah1st?.sp || "",
                      market_id: market.ah1st?.mkid || "",
                      home_price: market.ah1st?.hp || 0,
                      away_price: market.ah1st?.ap || 0,
                      home_hdp: market.ah1st?.h || 0,
                      away_hdp: market.ah1st?.a || 0,
                      home_outcome_id: market.ah1st?.och || "",
                      away_outcome_id: market.ah1st?.oca || "",
                      min_parlay: market.ah1st?.combo,
                    },
                  }
                : {}),
                ...(market?.ou1st ?
                  {
                    ou1st: {
                      sp: market.ou1st?.sp || "",
                      market_id: market.ou1st?.mkid || "",
                      over_price: market.ou1st?.op || 0,
                      under_price: market.ou1st?.up || 0,
                      over_hdp: market.ou1st?.o || 0,
                      under_hdp: market.ou1st?.u || 0,
                      over_outcome_id: market.ou1st?.oco || "",
                      under_outcome_id: market.ou1st?.ocu || "",
                      min_parlay: market.ou1st?.combo,
                    },
                  }
                : {}),
                ...(market?.x121st ?
                  {
                    x121st: {
                      sp: "",
                      market_id: market.x121st?.mkid || "",
                      home_price: market.x121st?.h || 0,
                      away_price: market.x121st?.a || 0,
                      draw_price: market.x121st?.d || 0,
                      home_outcome_id: market.x121st?.och || "",
                      away_outcome_id: market.x121st?.oca || "",
                      draw_outcome_id: market.x121st?.ocd || "",
                      min_parlay: market.x121st?.combo,
                    },
                  }
                : {}),
                ...(market?.ml1st ?
                  {
                    ml1st: {
                      sp: "",
                      market_id: market.ml1st?.mkid || "",
                      home_price: market.ml1st?.h || 0,
                      away_price: market.ml1st?.a || 0,
                      draw_price: market.ml1st?.d || 0,
                      home_outcome_id: market.ml1st?.och || "",
                      away_outcome_id: market.ml1st?.oca || "",
                      draw_outcome_id: market.ml1st?.ocd || "",
                      min_parlay: market.ml1st?.combo,
                    },
                  }
                : {}),
                // full time
                ...(market?.ah ?
                  {
                    ah: {
                      sp: market.ah?.sp || "",
                      market_id: market.ah?.mkid || "",
                      home_price: market.ah?.hp || 0,
                      away_price: market.ah?.ap || 0,
                      home_hdp: market.ah?.h || 0,
                      away_hdp: market.ah?.a || 0,
                      home_outcome_id: market.ah?.och || "",
                      away_outcome_id: market.ah?.oca || "",
                      min_parlay: market.ah?.combo,
                    },
                  }
                : {}),
                ...(market?.ou ?
                  {
                    ou: {
                      sp: market.ou?.sp || "",
                      market_id: market.ou?.mkid || "",
                      over_price: market.ou?.op || 0,
                      under_price: market.ou?.up || 0,
                      over_hdp: market.ou?.o || 0,
                      under_hdp: market.ou?.u || 0,
                      over_outcome_id: market.ou?.oco || "",
                      under_outcome_id: market.ou?.ocu || "",
                      min_parlay: market.ou?.combo,
                    },
                  }
                : {}),
                ...(market?.oe ?
                  {
                    oe: {
                      sp: "",
                      market_id: market.oe?.mkid || "",
                      odd_price: market.oe?.o || 0,
                      even_price: market.oe?.e || 0,
                      odd_hdp: market.oe?.o || 0,
                      even_hdp: market.oe?.e || 0,
                      odd_outcome_id: market.oe?.oco || "",
                      even_outcome_id: market.oe?.oce || "",
                      min_parlay: market.oe?.combo,
                    },
                  }
                : {}),
                ...(market?.x12 ?
                  {
                    x12: {
                      sp: "",
                      market_id: market.x12?.mkid || "",
                      home_price: market.x12?.h || 0,
                      away_price: market.x12?.a || 0,
                      draw_price: market.x12?.d || 0,
                      home_outcome_id: market.x12?.och || "",
                      away_outcome_id: market.x12?.oca || "",
                      draw_outcome_id: market.x12?.ocd || "",
                      min_parlay: market.x12?.combo,
                    },
                  }
                : {}),
                ...(market?.ml ?
                  {
                    ml: {
                      sp: "",
                      market_id: market.ml?.mkid || "",
                      home_price: market.ml?.h || 0,
                      away_price: market.ml?.a || 0,
                      draw_price: market.ml?.d || 0,
                      home_outcome_id: market.ml?.och || "",
                      away_outcome_id: market.ml?.oca || "",
                      draw_outcome_id: market.ml?.ocd || "",
                      min_parlay: market.ml?.combo,
                    },
                  }
                : {}),
              }))
            : [{}],
        })),
      };
    }) || [];

  return result.sort((a: League, b: League) => {
    return (
      a.is_favorite === b.is_favorite ? 0
      : a.is_favorite ? -1
      : 1
    );
  });
};
